import {
  ConnectionState,
  DisconnectReason,
  RemoteAudioTrack,
  Room,
  RoomEvent,
  ScreenSharePresets,
  Track,
  type RemoteTrack,
  type RemoteTrackPublication,
} from 'livekit-client';
import { create } from 'zustand';
import { callLink, createReconnectAlarm } from '@novachat/client';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { createDenoiser, denoiseContext } from '../lib/denoise';
import { sounds } from '../lib/sounds';
import { startCallDiagnostics } from '../lib/call-diagnostics';
import { useDevices } from './devices';
import { takeScreenPickerCancel } from './screen-picker';
import { sendRealtime } from './live';

export type VoiceConnection = 'idle' | 'connecting' | 'connected' | 'reconnecting';

export interface Publishing {
  mic: boolean;
  camera: boolean;
  screen: boolean;
}
const EVERYTHING: Publishing = { mic: true, camera: true, screen: true };

/** Reads the grants the server put into the token; before it says anything, the buttons stay usable (LiveKit still refuses). */
function publishing(room: Room): Publishing {
  const permissions = room.localParticipant.permissions;
  if (!permissions) return EVERYTHING;
  const may = (source: Track.Source) =>
    permissions.canPublish && (permissions.canPublishSources.length === 0 || permissions.canPublishSources.includes(Track.sourceToProto(source)));
  return { mic: may(Track.Source.Microphone), camera: may(Track.Source.Camera), screen: may(Track.Source.ScreenShare) };
}

interface VoiceState {
  channelId: string | null;
  room: Room | null;
  connection: VoiceConnection;
  /** Last problem to show (permission denied, kicked…). */
  notice: string | null;
  /** Room taken over by another device of ours: offered as "Buraya geri al". */
  rejoin: string | null;
  micOn: boolean;
  cameraOn: boolean;
  screenOn: boolean;
  deafened: boolean;
  /** What this call's token lets us publish; the channel roles decide (docs/plans/2026-11-03-channel-roles-design.md). */
  may: Publishing;
  /** LiveKit's measure of our own link in the call ('excellent', 'good', 'poor', 'lost', 'unknown'). */
  quality: string;
  /** Identities currently speaking (LiveKit active speakers). */
  speaking: ReadonlySet<string>;
  /** Identities whose screen share we chose to watch. */
  watching: ReadonlySet<string>;
  /** Bumped on every room change so views re-read the LiveKit room. */
  version: number;
  /** Per-person playback volume (0–1), kept in this browser. */
  volumes: Record<string, number>;
  setVolume(identity: string, volume: number): void;
  /** Everyone at once (0–1), on top of the per-person volumes. */
  outputVolume: number;
  setOutputVolume(volume: number): void;
  join(channelId: string): Promise<void>;
  leave(): Promise<void>;
  toggleMic(): Promise<void>;
  toggleCamera(): Promise<void>;
  toggleScreen(): Promise<void>;
  toggleDeafen(): void;
  watch(identity: string): void;
  unwatch(identity: string): void;
  dismissNotice(): void;
}

/**
 * Screen shares favour sharp text over motion (design §10, like Meet): at most 15 fps but up to
 * 6 Mbps, and the encoder keeps the resolution under pressure instead of blurring it. 30 fps at
 * ~3 Mbps turned blocky on every mouse move or page change.
 */
const SCREEN_ENCODING = { maxBitrate: 6_000_000, maxFramerate: 15 };

/** Phones encode H.264 in hardware; VP9 there runs in software and turns blocky. */
const SCREEN_CODEC = /iPhone|iPad|Android/.test(navigator.userAgent)
  ? ({ videoCodec: 'h264' } as const)
  : ({ videoCodec: 'vp9', backupCodec: true } as const);

function othersMicCue() {
  if (!useVoice.getState().deafened && useDevices.getState().othersMicCue) sounds.othersMicOn();
}

/** True while we mute or unmute ourselves, so a server-side mute can be told apart. */
let togglingMic = false;

const isScreen = (source: Track.Source) => source === Track.Source.ScreenShare || source === Track.Source.ScreenShareAudio;

/** Remote audio plays through detached <audio> elements kept here. */
const audioHost = document.createElement('div');
audioHost.hidden = true;
document.body.append(audioHost);

// The page really goes (closed, reloaded, signed out): leave the call so the others see it at once.
window.addEventListener('pagehide', () => void useVoice.getState().room?.disconnect());

export const useVoice = create<VoiceState>()((set, get) => {
  const bump = () => set((state) => ({ version: state.version + 1 }));

  /** Tells the server to re-read the room so everyone's sidebar updates (no LiveKit webhook for mutes). */
  const announce = () => {
    const { channelId } = get();
    if (channelId) sendRealtime({ type: 'voice.refresh', channelId });
  };

  /** Audio and cameras stream automatically; screen shares only after "Yayını izle" (design §10). */
  const subscribeByDefault = (publication: RemoteTrackPublication, identity: string) => {
    if (isScreen(publication.source)) {
      publication.setSubscribed(get().watching.has(identity));
    } else {
      publication.setSubscribed(true);
    }
  };

  const reset = (notice: string | null = null, rejoin: string | null = null) =>
    set({
      rejoin,
      channelId: null,
      room: null,
      connection: 'idle',
      micOn: false,
      cameraOn: false,
      screenOn: false,
      may: EVERYTHING,
      quality: 'unknown',
      speaking: new Set(),
      watching: new Set(),
      notice,
    });

  function wire(room: Room) {
    room
      .on(RoomEvent.TrackPublished, (publication, participant) => {
        subscribeByDefault(publication, participant.identity);
        if (publication.source === Track.Source.ScreenShare && !get().deafened) sounds.shareStart();
        // A first "mic on" publishes the track; later ones unmute it (below).
        if (publication.source === Track.Source.Microphone) othersMicCue();
        bump();
      })
      .on(RoomEvent.TrackMuted, (publication, participant) => {
        // Muted by the server: a moderator, or our roles changed (we mute ourselves through toggleMic).
        if (!participant.isLocal || publication.source !== Track.Source.Microphone || togglingMic || !get().micOn) return;
        set({ micOn: false, notice: get().may.mic ? 'Mikrofonunu görüşmeyi yöneten biri kapattı.' : 'Bu kanalda artık konuşma iznin yok; mikrofonun kapatıldı.' });
        sounds.micOff();
        announce();
      })
      .on(RoomEvent.TrackUnmuted, (publication, participant) => {
        if (!participant.isLocal && publication.source === Track.Source.Microphone) othersMicCue();
      })
      .on(RoomEvent.TrackUnpublished, (publication) => {
        if (publication.source === Track.Source.ScreenShare && !get().deafened) sounds.shareStop();
      })
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (track instanceof RemoteAudioTrack) audioHost.append(track.attach());
        applyVolumes(room);
        bump();
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        for (const element of track.detach()) element.remove();
        bump();
      })
      .on(RoomEvent.LocalTrackUnpublished, (publication) => {
        // The browser's own "Stop sharing" button ends a screen share without going through us.
        if (publication.source === Track.Source.ScreenShare) {
          set({ screenOn: false });
          announce();
        }
        bump();
      })
      .on(RoomEvent.ParticipantPermissionsChanged, (_previous, participant) => {
        if (!participant.isLocal) return;
        const may = publishing(room);
        set({ may });
        // Our roles changed mid-call: turn off what we may no longer send (the server already muted it).
        if (!may.camera && get().cameraOn) void room.localParticipant.setCameraEnabled(false).then(() => set({ cameraOn: false }));
        if (!may.screen && get().screenOn) void room.localParticipant.setScreenShareEnabled(false).then(() => set({ screenOn: false }));
      })
      .on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
        if (participant.isLocal) set({ quality });
      })
      .on(RoomEvent.ConnectionStateChanged, (state) => {
        if (state === ConnectionState.Reconnecting || state === ConnectionState.SignalReconnecting) set({ connection: 'reconnecting' });
        if (state === ConnectionState.Connected) set({ connection: 'connected' });
      })
      .on(RoomEvent.Disconnected, (reason) => {
        if (get().room !== room) return;
        for (const element of audioHost.querySelectorAll('audio')) element.remove();
        stopSpeakingMeter();
        reset(disconnectNotice(reason), reason === DisconnectReason.DUPLICATE_IDENTITY ? get().channelId : null);
      });
    // Short cues when people come and go, unless everything is muted.
    room
      .on(RoomEvent.ParticipantConnected, () => get().deafened || sounds.join())
      .on(RoomEvent.ParticipantDisconnected, () => get().deafened || sounds.leave());
    for (const event of [
      RoomEvent.TrackUnpublished,
      RoomEvent.TrackMuted,
      RoomEvent.TrackUnmuted,
      RoomEvent.ParticipantConnected,
      RoomEvent.ParticipantDisconnected,
      RoomEvent.LocalTrackPublished,
      RoomEvent.ConnectionQualityChanged,
    ] as const) {
      room.on(event, bump);
    }
  }

  async function withDevice(action: () => Promise<unknown>, denied: string) {
    try {
      await action();
    } catch (error) {
      const name = error instanceof Error ? error.name : '';
      set({ notice: name === 'NotAllowedError' || name === 'PermissionDeniedError' ? denied : 'Cihaz açılamadı.' });
      return false;
    }
    return true;
  }

  return {
    channelId: null,
    room: null,
    connection: 'idle',
    notice: null,
    rejoin: null,
    micOn: false,
    cameraOn: false,
    screenOn: false,
    may: EVERYTHING,
    quality: 'unknown',
    deafened: false,
    speaking: new Set(),
    watching: new Set(),
    version: 0,
    volumes: loadVolumes(),
    outputVolume: loadOutputVolume(),

    setVolume(identity, volume) {
      set((state) => ({ volumes: { ...state.volumes, [identity]: volume } }));
      saveVolumes(get().volumes);
      const { room } = get();
      if (room) applyVolumes(room);
    },

    setOutputVolume(outputVolume) {
      set({ outputVolume });
      try {
        localStorage.setItem(OUTPUT_KEY, String(outputVolume));
      } catch {
        // storage unavailable: lasts for this tab
      }
      const { room } = get();
      if (room) applyVolumes(room);
    },

    async join(channelId) {
      if (get().channelId === channelId) return;
      await get().leave();
      set({ channelId, connection: 'connecting', notice: null, rejoin: null });
      const prefs = useDevices.getState();
      const room = new Room({
        // LiveKit leaves on "beforeunload", which a download link also fires (the page stays): we leave on pagehide instead.
        disconnectOnPageLeave: false,
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: captureOptions(prefs),
        videoCaptureDefaults: { deviceId: prefs.cameraId },
        audioOutput: { deviceId: prefs.speakerId },
        publishDefaults: {
          videoCodec: 'vp9',
          backupCodec: true,
          screenShareEncoding: SCREEN_ENCODING,
        },
      });
      wire(room);
      try {
        const { url, token } = await client.api.voiceToken(channelId);
        if (get().channelId !== channelId) return; // left while connecting
        await room.connect(url, token, { autoSubscribe: false });
        for (const participant of room.remoteParticipants.values()) {
          for (const publication of participant.trackPublications.values()) subscribeByDefault(publication, participant.identity);
        }
        await room.startAudio().catch(() => undefined);
        set({ room, connection: 'connected', may: publishing(room) });
        startSpeakingMeter(room);
        sounds.join();
        bump();
        announce();
      } catch (error) {
        await room.disconnect();
        reset(errorMessage(error));
      }
    },

    async leave() {
      const { room } = get();
      if (!room) return reset();
      set({ room: null });
      for (const element of audioHost.querySelectorAll('audio')) element.remove();
      stopSpeakingMeter();
      await room.disconnect();
      sounds.leave();
      reset();
    },

    async toggleMic() {
      const { room, micOn, deafened, may } = get();
      if (!room) return;
      if (!micOn && !may.mic) return set({ notice: 'Bu kanalda konuşma iznin yok; yalnızca dinleyebilirsin.' });
      const next = !micOn;
      togglingMic = true;
      const ok = await withDevice(() => room.localParticipant.setMicrophoneEnabled(next), 'Mikrofon izni verilmedi. Tarayıcı ayarlarından izin ver.').finally(() => {
        togglingMic = false;
      });
      if (ok) {
        set({ micOn: next });
        (next ? sounds.micOn : sounds.micOff)();
        if (next) await applyDenoise(room);
        // Talking means listening too: turning the mic on ends deafen.
        if (next && deafened) {
          set({ deafened: false });
          applyVolumes(room);
        }
      }
      bump();
      announce();
    },

    async toggleCamera() {
      const { room, cameraOn, may } = get();
      if (!room) return;
      if (!cameraOn && !may.camera) return set({ notice: 'Bu kanalda kamera açma iznin yok.' });
      const ok = await withDevice(() => room.localParticipant.setCameraEnabled(!cameraOn), 'Kamera izni verilmedi. Tarayıcı ayarlarından izin ver.');
      if (ok) set({ cameraOn: !cameraOn });
      bump();
      announce();
    },

    async toggleScreen() {
      const { room, screenOn, may } = get();
      if (!room) return;
      if (!screenOn && !may.screen) return set({ notice: 'Bu kanalda ekran paylaşma iznin yok.' });
      takeScreenPickerCancel(); // forget an earlier one
      const ok = await withDevice(
        () =>
          room.localParticipant.setScreenShareEnabled(
            !screenOn,
            {
              audio: true,
              systemAudio: 'include',
              contentHint: 'detail',
              resolution: { ...ScreenSharePresets.h1080fps15.resolution, frameRate: 15 },
              selfBrowserSurface: 'exclude',
            },
            { ...SCREEN_CODEC, screenShareEncoding: SCREEN_ENCODING, degradationPreference: 'maintain-resolution' },
          ),
        'Ekran paylaşımına izin verilmedi.',
      );
      // Closing the desktop app's picker is a choice, not a failure.
      if (!ok && takeScreenPickerCancel()) set({ notice: null });
      if (ok) {
        set({ screenOn: !screenOn });
        (screenOn ? sounds.shareStop : sounds.shareStart)();
        // The next share starts as a tile again, not enlarged.
        if (screenOn) get().unwatch(room.localParticipant.identity);
      }
      bump();
      announce();
    },

    /** Silences everyone else and the own mic, like Discord's deafen. */
    toggleDeafen() {
      const { room, deafened, micOn } = get();
      const next = !deafened;
      set({ deafened: next });
      if (!room) return;
      applyVolumes(room);
      if (next && micOn) void get().toggleMic();
    },

    watch(identity) {
      set((state) => ({ watching: new Set(state.watching).add(identity) }));
      const participant = get().room?.remoteParticipants.get(identity);
      for (const publication of participant?.trackPublications.values() ?? []) {
        if (isScreen(publication.source)) publication.setSubscribed(true);
      }
    },

    unwatch(identity) {
      set((state) => {
        const watching = new Set(state.watching);
        watching.delete(identity);
        return { watching };
      });
      const participant = get().room?.remoteParticipants.get(identity);
      for (const publication of participant?.trackPublications.values() ?? []) {
        if (isScreen(publication.source)) publication.setSubscribed(false);
      }
    },

    dismissNotice: () => set({ notice: null, rejoin: null }),
  };
});

function disconnectNotice(reason: DisconnectReason | undefined): string | null {
  switch (reason) {
    case DisconnectReason.CLIENT_INITIATED:
      return null;
    case DisconnectReason.PARTICIPANT_REMOVED:
      return 'Görüşmeden çıkarıldın.';
    case DisconnectReason.ROOM_DELETED:
      return 'Bu görüşme kapatıldı.';
    // LiveKit allows one connection per identity: joining elsewhere replaces this one.
    case DisconnectReason.DUPLICATE_IDENTITY:
      return 'Bu görüşmeye başka bir cihazdan ya da sekmeden katıldın.';
    default:
      return 'Ses bağlantısı koptu.';
  }
}

/** Everyone's chosen volume times the overall one, or silence while deafened. */
function applyVolumes(room: Room) {
  const { deafened, volumes, outputVolume } = useVoice.getState();
  for (const participant of room.remoteParticipants.values()) {
    participant.setVolume(deafened ? 0 : (volumes[participant.identity] ?? 1) * outputVolume);
  }
}

const OUTPUT_KEY = 'novachat.output';
function loadOutputVolume(): number {
  try {
    const value = Number(localStorage.getItem(OUTPUT_KEY) ?? '1');
    return Number.isFinite(value) && value >= 0 && value <= 1 ? value : 1;
  } catch {
    return 1;
  }
}

const VOLUMES_KEY = 'novachat.volumes';
function loadVolumes(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(VOLUMES_KEY) ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}
function saveVolumes(volumes: Record<string, number>) {
  try {
    localStorage.setItem(VOLUMES_KEY, JSON.stringify(volumes));
  } catch {
    // storage unavailable: volumes last for this tab
  }
}

// Leave cleanly when the tab closes so others see us go right away.
window.addEventListener('pagehide', () => void useVoice.getState().room?.disconnect());

// Device changes made in settings apply to a call in progress right away.
useDevices.subscribe((prefs, previous) => {
  const room = useVoice.getState().room;
  if (!room) return;
  const ignore = () => undefined;
  if (prefs.micId !== previous.micId) void room.switchActiveDevice('audioinput', prefs.micId).catch(ignore);
  if (prefs.speakerId !== previous.speakerId) void room.switchActiveDevice('audiooutput', prefs.speakerId).catch(ignore);
  if (prefs.cameraId !== previous.cameraId) void room.switchActiveDevice('videoinput', prefs.cameraId).catch(ignore);
  if (prefs.denoise !== previous.denoise || prefs.autoGain !== previous.autoGain) {
    const mic = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.audioTrack;
    void mic?.restartTrack(captureOptions(prefs)).then(() => applyDenoise(room), ignore);
  }
});

/**
 * Browser processing. Its own suppressor stays on under RNNoise and GTCRN: they keep steady tones
 * (they look like voice), and a feedback tone between two speakers then lasted 10-15 s.
 * Off under DeepFilterNet3: it cuts steady tones itself (~50 dB), the howl guard stays behind it, and steady typing dropped 45.7 dB with it off vs 36.1 dB on.
 */
function captureOptions(prefs: { micId: string; denoise: string; autoGain: boolean }) {
  return { deviceId: prefs.micId, echoCancellation: true, noiseSuppression: prefs.denoise !== 'off' && prefs.denoise !== 'deepfilter', autoGainControl: prefs.autoGain };
}

const activeModel = new WeakMap<object, string | null>();

/** Puts the chosen model on the mic track (or takes it off); falls back to the browser's own. */
async function applyDenoise(room: Room) {
  const mic = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.audioTrack;
  if (!mic) return;
  const { denoise } = useDevices.getState();
  const model = denoise === 'browser' || denoise === 'off' ? null : denoise;
  if ((activeModel.get(mic) ?? null) === model) return;
  try {
    if (!model) {
      await mic.stopProcessor();
    } else {
      await mic.setAudioContext(denoiseContext());
      await mic.setProcessor(createDenoiser(model));
    }
    activeModel.set(mic, model);
  } catch {
    activeModel.set(mic, null);
    useVoice.setState({ notice: 'Gelişmiş gürültü engelleme bu tarayıcıda açılamadı. Tarayıcının kendi engellemesi kullanılıyor.' });
    await mic.restartTrack({ ...captureOptions(useDevices.getState()), noiseSuppression: true }).catch(() => undefined);
  }
}

/**
 * Who is speaking, measured locally from the audio itself every 80 ms, like Discord. LiveKit's
 * server-side active speakers arrive ~0.5–1 s late (aggregated and smoothed), so the ring lagged.
 */
const SPEAKING_LEVEL = 0.015;
const HOLD_MS = 350;
/** How much of a level survives one 80 ms tick without a louder sound: the ring eases back between syllables instead of flickering. */
const RELEASE = 0.7;
let meter: { timer: ReturnType<typeof setInterval>; analysers: Map<MediaStreamTrack, AnalyserNode> } | null = null;
let localLevel = 0;

/** The own mic's loudness (0–1) as last measured; 0 while it is off. For the input meter. */
export const micLevel = () => localLevel;

/**
 * Everyone's loudness (0–1) by identity, on the mic meter's scale, refreshed with the speaking set.
 * Its own store, so the call screen (which reads the whole voice store) does not re-render twelve
 * times a second; the speaking ring reads it straight into a CSS variable.
 */
export const useLevels = create<{ levels: Readonly<Record<string, number>> }>(() => ({ levels: {} }));

const sameLevels = (a: Readonly<Record<string, number>>, b: Readonly<Record<string, number>>) => {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k]);
};

/** The call's diagnostics recorder (lib/call-diagnostics.ts), running with the meter. */
let stopDiagnostics: (() => void) | null = null;

function startSpeakingMeter(room: Room) {
  stopSpeakingMeter();
  stopDiagnostics = startCallDiagnostics(room);
  const ctx = denoiseContext();
  void ctx.resume();
  const analysers = new Map<MediaStreamTrack, AnalyserNode>();
  const lastHeard = new Map<string, number>();
  const buffer = new Float32Array(1024);

  const level = (track: MediaStreamTrack) => {
    let analyser = analysers.get(track);
    if (!analyser) {
      analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(new MediaStream([track])).connect(analyser);
      analysers.set(track, analyser);
    }
    analyser.getFloatTimeDomainData(buffer);
    let sum = 0;
    for (const v of buffer) sum += v * v;
    return Math.sqrt(sum / buffer.length);
  };

  const timer = setInterval(() => {
    const now = performance.now();
    const live = new Set<MediaStreamTrack>();
    const people = [room.localParticipant, ...room.remoteParticipants.values()];
    const previous = useLevels.getState().levels;
    const levels: Record<string, number> = {};
    localLevel = 0;
    for (const participant of people) {
      const publication = participant.getTrackPublication(Track.Source.Microphone);
      const track = publication?.track?.mediaStreamTrack;
      if (!track || publication.isMuted || track.readyState !== 'live') continue;
      live.add(track);
      const loudness = level(track);
      const instant = Math.min(1, loudness * 4);
      if (participant === room.localParticipant) localLevel = instant;
      if (loudness > SPEAKING_LEVEL) lastHeard.set(participant.identity, now);
      levels[participant.identity] = Math.max(instant, (previous[participant.identity] ?? 0) * RELEASE);
    }
    if (!sameLevels(previous, levels)) useLevels.setState({ levels });
    for (const [track, analyser] of analysers) {
      if (!live.has(track)) {
        analyser.disconnect();
        analysers.delete(track);
      }
    }
    const speaking = new Set([...lastHeard].filter(([, at]) => now - at < HOLD_MS).map(([identity]) => identity));
    const current = useVoice.getState().speaking;
    if (speaking.size !== current.size || [...speaking].some((id) => !current.has(id))) useVoice.setState({ speaking });
  }, 80);
  meter = { timer, analysers };
}

function stopSpeakingMeter() {
  stopDiagnostics?.();
  stopDiagnostics = null;
  localLevel = 0;
  useLevels.setState({ levels: {} });
  if (!meter) return;
  clearInterval(meter.timer);
  for (const analyser of meter.analysers.values()) analyser.disconnect();
  meter = null;
}

// While the call is cut off: a sound round after round until it is back, then a short "connected"
// (packages/client/src/reconnect.ts). Not outside a call, and not with every sound turned off.
const reconnectAlarm = createReconnectAlarm({ cue: sounds.reconnecting, reconnected: sounds.reconnected });
useVoice.subscribe((state) => reconnectAlarm.update(callLink(state)));
